/**
 * Что сказать человеку сразу после нажатия сквозной кнопки (S50).
 *
 * Модуль отдельный, потому что читателей стало два: полоса конвейера на экране
 * папки и кнопка «Распознать» на экране разметки. Обе зовут один маршрут
 * `POST /folders/{id}/check`, и разойтись в формулировке им нельзя: человек
 * видит их в одном сеансе работы и по разнице слов решит, что действия разные.
 *
 * Функция чистая и без React намеренно — её проверяют модульным тестом, а не
 * рендером.
 */
import type { CheckPipelineResult } from '../../api/endpoints.js';
import { plural } from '../checks/grouping.js';

const STAGE_STARTED_LABEL: Readonly<Record<string, string>> = {
  recognition: 'Распознавание запущено: дальше анализ и проверки пойдут сами',
  analysis: 'Распознано; запущены разбор документов и реквизитов, за ними — проверки',
  checks: 'Запущен прогон правил по уже разобранным документам',
};

/**
 * Что сказать после нажатия — с числом перечитываемых листов (S40).
 *
 * Число обязательно там, где оно ноль: «Распознать только ошибки» на комплекте
 * без ошибок ничего не перечитывает и уходит на прогон правил. Без этой фразы
 * нажатие выглядело бы как бездействие кнопки, и человек нажал бы ещё раз.
 */
export function startedLabel(result: CheckPipelineResult): string {
  // Заказ идущему прогону — не старт новой работы, и сказать об этом надо
  // первым делом: иначе человек ждёт, что счётчик страниц пойдёт с нуля.
  if (result.continuedRun === true) {
    return 'Распознавание уже идёт: портал доведёт комплект до проверки сам';
  }
  if (result.stage === 'recognition' && (result.retriedPages ?? 0) > 0) {
    const pages = result.retriedPages ?? 0;
    return `Перечитываем ${String(pages)} ${plural(pages, 'лист', 'листа', 'листов')}: остальное перенесётся из прошлого прогона`;
  }
  if (result.stage !== 'recognition' && result.retriedPages === 0) {
    return 'Перечитывать нечего: запущен прогон правил по уже распознанному комплекту';
  }
  return STAGE_STARTED_LABEL[result.stage] ?? 'Обработка запущена';
}
